/**
 * ARVEXIS — USAGE THIS PERIOD
 * ===========================
 *
 * The canonical member-facing capacity surface: how much of this period's Talk and Message
 * capacity has been used, what the allowance is, how much is left, and when it resets.
 *
 * Rules this file holds to, deliberately:
 *   - Every figure comes from GET /api/usage/summary. NOTHING is computed, estimated or
 *     hard-coded here. There is no second usage model and no UI-specific arithmetic beyond
 *     rounding seconds DOWN to whole minutes, so the remaining figure is never overstated.
 *   - An allowance that has not been configured renders NOTHING for that capability. No
 *     placeholder, no dash, no invented number.
 *   - Exhausted states are neutral. They report capacity and say plainly that the relationship
 *     is untouched. No urgency, no countdown, no attachment language, no guilt, no upsell
 *     pressure. This surface is not an engagement lever.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Loader2, Mic, MessageCircle } from 'lucide-react';
import DreamsShell from './DreamsShell';
import useFullScreenSurface from '../hooks/useFullScreenSurface';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Seconds to whole minutes. Time REMAINING is rounded down — a member must never be shown a
// minute they cannot have. Time USED is rounded up, so the two figures agree with the
// allowance instead of quietly losing the partial minute between them. Enforcement itself is
// exact to the second; this is presentation only.
const minutesLeft = (seconds) => Math.floor(Math.max(0, Number(seconds) || 0) / 60);
const minutesUsed = (seconds) => Math.ceil(Math.max(0, Number(seconds) || 0) / 60);

// Thousands grouped in the member's own language, so a four-figure allowance reads as one
// number instead of wrapping onto two lines.
const grouped = (value, lang) =>
  Number(value || 0).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US');

const formatResetDate = (iso, lang) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US',
    { month: 'long', day: 'numeric' });
};

const Figure = ({ label, value, testId }) => (
  <div className="usg__figure">
    <span className="usg__figlabel">{label}</span>
    <span className="usg__figvalue" data-testid={testId}>{value}</span>
  </div>
);

// =============================================================================
// ONE CAPABILITY — Talk or Message, the same shape, from the same contract
// =============================================================================
const CapabilityCard = ({ snapshot, kind, lang, index }) => {
  if (!snapshot || !snapshot.allowance_configured) return null;

  const isTalk = kind === 'talk';
  const resets = formatResetDate(snapshot.period_end, lang);
  const used = isTalk ? minutesUsed(snapshot.used) : snapshot.used;
  const allowance = isTalk ? minutesLeft(snapshot.allowance) : snapshot.allowance;
  const remaining = isTalk ? minutesLeft(snapshot.remaining) : snapshot.remaining;

  const unit = isTalk
    ? (lang === 'es' ? 'min' : 'min')
    : (lang === 'es' ? 'mensajes' : 'messages');

  const title = isTalk
    ? (lang === 'es' ? 'Talk' : 'Talk')
    : (lang === 'es' ? 'Message' : 'Message');

  const exhaustedCopy = isTalk
    ? (lang === 'es'
      ? 'Has usado el tiempo de Talk de este período. Tus recuerdos, tus conversaciones y todo lo que Arvexis sabe de ti permanecen intactos.'
      : "You've used this period's Talk time. Your memories, your conversations and everything Arvexis knows about you stay exactly as they are.")
    : (lang === 'es'
      ? 'Has usado los mensajes de este período. Tu historial, tus Recuerdos Compartidos y todo lo que Arvexis sabe de ti permanecen intactos.'
      : "You've used this period's messages. Your history, your Shared Memories and everything Arvexis knows about you stay exactly as they are.");

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="usg__card"
      data-testid={`usage-${kind}-card`}
    >
      <div className="usg__head">
        <span className="usg__glyph">
          {isTalk ? <Mic size={18} strokeWidth={1.6} /> : <MessageCircle size={18} strokeWidth={1.6} />}
        </span>
        <span className="usg__title" data-testid={`usage-${kind}-title`}>{title}</span>
      </div>

      <div className="usg__figures">
        <Figure
          label={lang === 'es' ? 'Usado' : 'Used'}
          value={`${grouped(used, lang)} ${unit}`}
          testId={`usage-${kind}-used`}
        />
        <Figure
          label={lang === 'es' ? 'Incluido' : 'Allowance'}
          value={`${grouped(allowance, lang)} ${unit}`}
          testId={`usage-${kind}-allowance`}
        />
        <Figure
          label={lang === 'es' ? 'Restante' : 'Remaining'}
          value={`${grouped(remaining, lang)} ${unit}`}
          testId={`usage-${kind}-remaining`}
        />
      </div>

      {resets && (
        <p className="usg__reset" data-testid={`usage-${kind}-reset`}>
          {snapshot.period_basis === 'trial'
            ? (lang === 'es' ? `Meet ARVEXIS termina el ${resets}` : `Meet ARVEXIS ends on ${resets}`)
            : (lang === 'es' ? `Se reinicia el ${resets}` : `Resets on ${resets}`)}
        </p>
      )}

      {snapshot.exhausted && (
        <p className="usg__exhausted" data-testid={`usage-${kind}-exhausted`}>
          {exhaustedCopy}
        </p>
      )}
    </motion.div>
  );
};

// =============================================================================
// COMPACT PRESENTATION — the same truth, on the Premium surface
// =============================================================================
export const UsageCompact = ({ lang = 'en' }) => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/usage/summary`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (alive) setSummary(data); })
      .catch(() => { /* metering unavailable: show nothing rather than approximate */ });
    return () => { alive = false; };
  }, []);

  if (!summary || !summary.any_allowance_configured) return null;

  const row = (snapshot, kind) => {
    if (!snapshot?.allowance_configured) return null;
    const isTalk = kind === 'talk';
    const remaining = isTalk ? minutesLeft(snapshot.remaining) : snapshot.remaining;
    const allowance = isTalk ? minutesLeft(snapshot.allowance) : snapshot.allowance;
    const unit = isTalk ? 'min' : (lang === 'es' ? 'mensajes' : 'messages');
    return (
      <div className="usgc__row" data-testid={`usage-compact-${kind}`}>
        <span className="usgc__label">{isTalk ? 'Talk' : 'Message'}</span>
        <span className="usgc__value" data-testid={`usage-compact-${kind}-value`}>
          {lang === 'es'
            ? `${grouped(remaining, lang)} de ${grouped(allowance, lang)} ${unit} restantes`
            : `${grouped(remaining, lang)} of ${grouped(allowance, lang)} ${unit} left`}
        </span>
      </div>
    );
  };

  const resets = formatResetDate(summary.period_end, lang);
  // A trial allowance is a total for the 7 days, so it does not reset — it ends with the trial.
  const isTrialWindow = (summary.message?.period_basis || summary.talk?.period_basis) === 'trial';

  return (
    <div className="sub__card" data-testid="usage-compact-card">
      <h2 className="sub__cardtitle">
        {lang === 'es' ? 'Uso de este período' : 'Usage this period'}
      </h2>
      {row(summary.talk, 'talk')}
      {row(summary.message, 'message')}
      {resets && (
        <p className="usgc__reset" data-testid="usage-compact-reset">
          {isTrialWindow
            ? (lang === 'es' ? `Meet ARVEXIS termina el ${resets}` : `Meet ARVEXIS ends on ${resets}`)
            : (lang === 'es' ? `Se reinicia el ${resets}` : `Resets on ${resets}`)}
        </p>
      )}
      <style>{`
        .usgc__row {
          display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
          padding: 9px 0; border-bottom: 1px solid rgba(244, 239, 231, 0.06);
          font-family: 'Inter', system-ui, sans-serif;
        }
        .usgc__row:last-of-type { border-bottom: none; }
        .usgc__label { font-size: 14px; color: rgba(244, 239, 231, 0.9); }
        .usgc__value { font-size: 13.5px; color: rgba(232, 197, 143, 0.9); }
        .usgc__reset {
          margin-top: 10px; font-size: 12.5px; color: rgba(244, 239, 231, 0.45);
          font-family: 'Inter', system-ui, sans-serif;
        }
      `}</style>
    </div>
  );
};

// =============================================================================
// THE CANONICAL DETAILED SURFACE
// =============================================================================
const UsageThisPeriod = ({ lang = 'en', onClose, activePath = '/profile' }) => {
  useFullScreenSurface(true, onClose);
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/usage/summary`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load usage');
      setSummary(await res.json());
    } catch (err) {
      setError(lang === 'es' ? 'No se pudo cargar el uso' : 'Could not load usage');
    } finally {
      setIsLoading(false);
    }
  };

  const configured = summary?.any_allowance_configured;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // the foreground is handed back at once: fading out cross-faded the still-mounted page beneath through this surface
      className="utility-overlay z-[150] utility-surface utility-surface--atmospheric"
      data-testid="usage-period-modal"
    >
      <DreamsShell lang={lang} activePath={activePath}>
        <header
          className="flex items-center gap-4 px-5 account-mobile-header"
          style={{
            paddingTop: 'calc(env(safe-area-inset-top, 47px) + 12px)',
            paddingBottom: '12px'
          }}
        >
          <button
            onClick={onClose}
            className="rounded-full flex items-center justify-center shrink-0"
            style={{ width: 44, height: 44, background: 'transparent',
                     border: '1px solid rgba(232, 197, 143, 0.38)' }}
            aria-label={lang === 'es' ? 'Volver' : 'Back'}
            data-testid="usage-period-close"
          >
            <ChevronLeft className="w-5 h-5" style={{ color: 'rgba(232, 197, 143, 0.9)' }} />
          </button>
          <span
            className="account-wordmark"
            style={{ fontSize: '12px', letterSpacing: '0.42em', color: 'rgba(244, 239, 231, 0.8)',
                     margin: '0 auto', paddingRight: '44px' }}
            data-testid="usage-period-wordmark"
          >
            ARVEXIS
          </span>
        </header>

        <div className="utility-head account-col" data-testid="usage-period-head">
          <p className="utility-head__eyebrow" data-testid="usage-period-eyebrow">
            {lang === 'es' ? 'USO DE ESTE PERÍODO' : 'USAGE THIS PERIOD'}
          </p>
          <h1 className="utility-head__title" data-testid="usage-period-title">
            {lang === 'es' ? 'Uso de este período' : 'Usage this period'}
          </h1>
          <p className="utility-head__sub" data-testid="usage-period-subtitle">
            {lang === 'es'
              ? 'Cuánta capacidad has usado y cuándo se reinicia.'
              : 'How much capacity you have used, and when it resets.'}
          </p>
        </div>

        <main className="account-col" style={{ padding: '4px 20px 48px' }} data-testid="usage-period-main">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#E8C58F' }} />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="usg__empty">{error}</p>
              <button onClick={load} className="usg__retry" data-testid="usage-period-retry">
                {lang === 'es' ? 'Intentar de nuevo' : 'Try again'}
              </button>
            </div>
          ) : !configured ? (
            <div className="text-center py-12" data-testid="usage-period-unconfigured">
              <p className="usg__empty">
                {lang === 'es'
                  ? 'No hay límites de uso en efecto en tu cuenta.'
                  : 'There are no usage limits in effect on your account.'}
              </p>
            </div>
          ) : (
            <div className="usg__stack">
              <CapabilityCard snapshot={summary.talk} kind="talk" lang={lang} index={0} />
              <CapabilityCard snapshot={summary.message} kind="message" lang={lang} index={1} />
            </div>
          )}
        </main>
      </DreamsShell>

      <style>{`
        .usg__stack { display: flex; flex-direction: column; gap: 12px; }
        .usg__card {
          padding: 18px; border-radius: 18px; background: rgba(255, 255, 255, 0.028);
          border: 1px solid rgba(244, 239, 231, 0.10);
          font-family: 'Inter', system-ui, sans-serif;
        }
        .usg__head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
        .usg__glyph {
          flex-shrink: 0; width: 38px; height: 38px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          border: 1px solid rgba(232, 197, 143, 0.42); color: #E8C58F;
        }
        .usg__title { font-size: 16px; color: #F4EFE7; }
        .usg__figures {
          display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px;
        }
        .usg__figure { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
        .usg__figlabel {
          font-size: 10.5px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase;
          color: rgba(232, 197, 143, 0.7);
        }
        .usg__figvalue { font-size: 15px; color: #F4EFE7; }
        .usg__reset {
          margin-top: 14px; font-size: 13px; color: rgba(244, 239, 231, 0.48);
        }
        .usg__exhausted {
          margin-top: 12px; padding-top: 12px; font-size: 13.5px; line-height: 1.6;
          color: rgba(244, 239, 231, 0.62);
          border-top: 1px solid rgba(244, 239, 231, 0.08);
        }
        .usg__empty {
          font-size: 14px; line-height: 1.6; color: rgba(244, 239, 231, 0.45);
          font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
        }
        .usg__retry {
          margin-top: 12px; font-size: 13px; color: #E8C58F; background: none; border: none;
          cursor: pointer; text-decoration: underline;
        }
      `}</style>
    </motion.div>
  );
};

export default UsageThisPeriod;
